import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE, getAuthToken } from "../../utils/api";

const EncountersDebugPage: React.FC = () => {
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [testResponse, setTestResponse] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    runDiagnostics();
  }, []);

  const runDiagnostics = async () => {
    const info: any = {
      hasAuthToken: false,
      authToken: null,
      user: null,
      apiBase: API_BASE,
      timestamp: new Date().toISOString(),
    };

    // Check localStorage
    const token = getAuthToken();
    info.hasAuthToken = !!token;
    info.authToken = token ? `${token.substring(0, 10)}...` : null;

    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        info.user = JSON.parse(userStr);
      } catch (e) {
        info.userParseError = (e as Error).message;
      }
    }

    setDebugInfo(info);

    // Test API call
    if (token) {
      try {
        console.log("🔍 Testing API call to:", `${API_BASE}/soap-notes/`);
        const response = await axios.get(`${API_BASE}/soap-notes/`);
        console.log("✅ API Response:", response.data);
        setTestResponse({
          status: "success",
          data: response.data,
        });
      } catch (error: any) {
        console.error("❌ API Error:", error);
        setTestResponse({
          status: "error",
          error: error.message,
          response: error.response?.data,
          statusCode: error.response?.status,
        });
      }
    } else {
      setTestResponse({
        status: "error",
        error: "No auth token found. Please login first.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h1 className="text-2xl font-bold mb-4">
            Encounters Page - Debug Info
          </h1>

          <button
            onClick={() => navigate("/encounters")}
            className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            ← Back to Encounters
          </button>

          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold mb-2">
                Authentication Status
              </h2>
              <div className="bg-gray-50 p-4 rounded">
                <p className="mb-2">
                  <strong>Has Auth Token:</strong>{" "}
                  <span
                    className={
                      debugInfo.hasAuthToken ? "text-green-600" : "text-red-600"
                    }
                  >
                    {debugInfo.hasAuthToken ? "✅ Yes" : "❌ No"}
                  </span>
                </p>
                <p className="mb-2">
                  <strong>Token Preview:</strong>{" "}
                  {debugInfo.authToken || "None"}
                </p>
                <p>
                  <strong>User:</strong>{" "}
                  {debugInfo.user
                    ? `${debugInfo.user.username} (${debugInfo.user.user_type})`
                    : "Not logged in"}
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-2">API Configuration</h2>
              <div className="bg-gray-50 p-4 rounded">
                <p className="mb-2">
                  <strong>API Base URL:</strong> {debugInfo.apiBase}
                </p>
                <p>
                  <strong>Encounters Endpoint:</strong> {debugInfo.apiBase}
                  /soap-notes/
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-2">API Test Response</h2>
              <div className="bg-gray-50 p-4 rounded">
                {testResponse ? (
                  <div>
                    <p className="mb-2">
                      <strong>Status:</strong>{" "}
                      <span
                        className={
                          testResponse.status === "success"
                            ? "text-green-600"
                            : "text-red-600"
                        }
                      >
                        {testResponse.status}
                      </span>
                    </p>
                    {testResponse.status === "success" ? (
                      <div>
                        <p>
                          <strong>Encounters Count:</strong>{" "}
                          {testResponse.data?.results?.length || 0}
                        </p>
                        <pre className="mt-2 p-2 bg-white border rounded text-xs overflow-auto max-h-96">
                          {JSON.stringify(testResponse.data, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div>
                        <p className="text-red-600 mb-2">
                          <strong>Error:</strong> {testResponse.error}
                        </p>
                        {testResponse.statusCode && (
                          <p className="mb-2">
                            <strong>Status Code:</strong>{" "}
                            {testResponse.statusCode}
                          </p>
                        )}
                        {testResponse.response && (
                          <pre className="mt-2 p-2 bg-white border rounded text-xs overflow-auto">
                            {JSON.stringify(testResponse.response, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">Running test...</p>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-2">Troubleshooting</h2>
              <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
                <ul className="list-disc list-inside space-y-2 text-sm">
                  {!debugInfo.hasAuthToken && (
                    <li className="text-red-600">
                      ❌ <strong>Not authenticated!</strong> Please{" "}
                      <button
                        onClick={() => navigate("/login")}
                        className="underline text-blue-600"
                      >
                        login first
                      </button>
                    </li>
                  )}
                  {debugInfo.hasAuthToken &&
                    testResponse?.status === "error" && (
                      <li className="text-red-600">
                        ❌ <strong>API Error:</strong> {testResponse.error}
                      </li>
                    )}
                  {debugInfo.hasAuthToken &&
                    testResponse?.status === "success" &&
                    testResponse?.data?.results?.length === 0 && (
                      <li className="text-yellow-700">
                        ⚠️ <strong>No encounters found.</strong> Try creating
                        one from the iOS app first.
                      </li>
                    )}
                  {debugInfo.hasAuthToken &&
                    testResponse?.status === "success" &&
                    testResponse?.data?.results?.length > 0 && (
                      <li className="text-green-600">
                        ✅ <strong>All good!</strong> Found{" "}
                        {testResponse.data.results.length} encounter(s). The
                        encounters page should work.
                      </li>
                    )}
                  <li className="text-gray-600">
                    💡 Check browser console (F12) for additional errors
                  </li>
                  <li className="text-gray-600">
                    💡 Verify backend is running at: {debugInfo.apiBase}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t">
            <button
              onClick={runDiagnostics}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              🔄 Re-run Diagnostics
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EncountersDebugPage;
